import { Component, AfterViewInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

interface Location {
    name: string;
    lat: number;
    lng: number;
    description: string;
    status: 'online' | 'abnormal' | 'offline';
    production: number;
    batteryLevel: string;
}

@Component({
    selector: 'app-plant-map',
    imports: [CommonModule, NgIf, MatCardModule, MatButtonModule, MatMenuModule, TranslatePipe],
    templateUrl: './plant-map.html',
    styleUrl: './plant-map.scss'
})
export class PlantMap implements AfterViewInit, OnDestroy {

    private map: any;
    private L: any;
    public isBrowser: boolean;
    private ctrlPressed = false;
    private keydownHandler!: (e: KeyboardEvent) => void;
    private keyupHandler!: (e: KeyboardEvent) => void;
    private wheelHandler!: (e: WheelEvent) => void;

    private allBounds: any[] = [];

    private locations: Location[] = [
        { name: 'TPJ4C, Aarhus', lat: 56.1629, lng: 10.2039, description: 'S-N: TPJ4CCU013', status: 'online', production: 10.6, batteryLevel: '54%' },
        { name: 'GZD19, Thisted', lat: 56.9578, lng: 8.7092, description: 'S-N: GZD191204F', status: 'online', production: 13.02, batteryLevel: 'No battery' },
        { name: 'HAD18, Roskilde', lat: 55.6426, lng: 12.0901, description: 'S-N: HAD1850037', status: 'abnormal', production: 0, batteryLevel: 'No battery' },
        { name: 'LQD09, Odense', lat: 55.4052, lng: 10.4049, description: 'S-N: LQD092103A', status: 'online', production: 21.7, batteryLevel: '100%' },
        { name: 'MDD09, Esbjerg', lat: 55.4796, lng: 8.4528, description: 'S-N: MDD0921101', status: 'offline', production: 0, batteryLevel: '0%' }
    ];

    constructor(@Inject(PLATFORM_ID) private platformId: object, private translate: TranslateService) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    async ngAfterViewInit() {
        if (!this.isBrowser) return;

        const leafletModule = await import('leaflet');
        const L = (leafletModule as any).default ?? leafletModule;
        this.L = L;

        if (L.Icon && L.Icon.Default) {
            delete (L.Icon.Default.prototype as any)._getIconUrl;

            L.Icon.Default.mergeOptions ({
                iconRetinaUrl: 'images/icons/marker-icon-2x.png',
                iconUrl: 'images/icons/marker-icon.png',
                shadowUrl: 'images/icons/marker-shadow.png'
            });
        }

        this.initMap(L);
        this.addMarkers(L);
        this.setupCtrlZoomHandler();
        this.addResetControl();
    }

    private initMap(L: any): void {
        this.map = L.map('map', {
            center: [55.58, 11.46],
            zoom: 6,
            zoomControl: true,
            scrollWheelZoom: false,
            dragging: true
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            crossOrigin: 'anonymous'
        }).addTo(this.map);
    }

    public get onlinePlants(): number {
        return this.locations.filter(location => location.status === 'online').length;
    }

    public get abnormalPlants(): number {
        return this.locations.filter(location => location.status === 'abnormal').length;
    }

    public get offlinePlants(): number {
        return this.locations.filter(location => location.status === 'offline').length;
    }

    public get totalProduction(): number {
        return this.locations.reduce((total, location) => total + location.production, 0);
    }

    private getIconByStatus(status: 'online' | 'abnormal' | 'offline') {
        return this.L.divIcon({
            className: `fleet-marker fleet-marker--${status}`,
            html: `
                <span class="fleet-marker__pulse"></span>
                <span class="fleet-marker__pin">
                    <i class="material-symbols-outlined">solar_power</i>
                </span>
            `,
            iconSize: [44, 44],
            iconAnchor: [22, 22],
            popupAnchor: [0, -22]
        });
    }

    private addMarkers(L: any): void {
        this.allBounds = [];

        this.locations.forEach(loc => {
            const icon = this.getIconByStatus(loc.status);
            const marker = L.marker([loc.lat, loc.lng], { icon }).addTo(this.map);

            this.allBounds.push([loc.lat, loc.lng]);

            const popupContent = `
                <div class="fleet-popup">
                    <div class="fleet-popup__status fleet-popup__status--${loc.status}">${this.translate.instant(this.statusKey(loc.status))}</div>
                    <strong>${loc.name}</strong>
                    <span>${loc.description}</span>
                    <div class="fleet-popup__grid">
                        <div>
                            <small>${this.translate.instant('Production')}</small>
                            <b>${loc.production.toFixed(2)} kWh</b>
                        </div>
                        <div>
                            <small>${this.translate.instant('pie_dashboard.common.battery')}</small>
                            <b>${loc.batteryLevel === 'No battery' ? this.translate.instant('pie_dashboard.common.no_battery') : loc.batteryLevel}</b>
                        </div>
                    </div>
                </div>
            `;

            /* With View details button - removed for now, since it doesn't work yet */
            /* const popupContent = `
                <div style="text-align:center;">
                    <b>${loc.name}</b><br>
                    <small>${loc.description}</small><br>
                    <span style="color:${
                        loc.status === 'online' ? 'green' : 
                        loc.status === 'abnormal' ? 'red' : 'blue'
                    };">${loc.status.toUpperCase()}</span><br>
                    <a href="#" id="link-${loc.description}" style="text-decoration:underline;color:#2F7F79;font-weight:700;">
                        View Details
                    </a>
                </div>
            `; */

            marker.bindPopup(popupContent);

            marker.bindTooltip(`${loc.name}`, {
                permanent: false,
                direction: 'top',
                offset: [0, -10],
            });
        });

        if (this.allBounds.length > 0) {
            setTimeout(() => {
                this.map.flyToBounds(this.allBounds, {
                    padding: [40, 40],
                    maxZoom: 10,
                    duration: 1
                });
            }, 500);
        }
    }

    private statusKey(status: 'online' | 'abnormal' | 'offline'): string {
        if (status === 'online') return 'Online';
        if (status === 'abnormal') return 'Abnormal';
        return 'Offline';
    }

    public resetMapView(): void {
        if (this.map && this.allBounds.length > 0) {
            this.map.flyToBounds(this.allBounds, {
                padding: [40, 40],
                maxZoom: 10,
                duration: 0.5
            });
        }
    }

    private addResetControl(): void {
        if (!this.L || !this.map) return;

        const resetControl = this.L.Control.extend({
            options: { position: 'topright' },

            onAdd: (map:any) => {
                const container = this.L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
                container.style.backgroundColor = 'white';
                container.style.width = '30px';
                container.style.height = '30px';
                container.style.cursor = 'pointer';
                container.style.display = 'flex';
                container.style.alignItems = 'center';
                container.style.justifyContent = 'center';
                container.style.fontSize = '18px';
                container.title = this.translate.instant('Reset_map_view');
                container.innerHTML = '⟳';

                container.onclick = () => this.resetMapView();

                return container;
            }
        });

        this.map.addControl(new resetControl());
    }

    private setupCtrlZoomHandler(): void {
        let hintTimeout: any;

        this.keydownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Control' && !this.ctrlPressed) {
                this.ctrlPressed = true;
                this.map.scrollWheelZoom.enable();
                this.showCtrlHint(false);
            }
        };

        this.keyupHandler = (e: KeyboardEvent) => {
            if (e.key === 'Control') {
                this.ctrlPressed = false;
                this.map.scrollWheelZoom.disable();
            }
        };

        this.wheelHandler = (e: WheelEvent) => {
            if (!this.ctrlPressed) {
                e.preventDefault();

                if (hintTimeout) clearTimeout(hintTimeout);

                this.showCtrlHint(true);

                hintTimeout = setTimeout(() => {
                this.showCtrlHint(false);
                hintTimeout = null;
                }, 2000);
            }
        };

        // event listeners
        window.addEventListener('keydown', this.keydownHandler);
        window.addEventListener('keyup', this.keyupHandler);
        this.map.getContainer().addEventListener('wheel', this.wheelHandler);
    }

    public showCtrlOverlay = false;
    private showCtrlHint(show: boolean): void {
        this.showCtrlOverlay = show;
    }

    ngOnDestroy(): void {
        if (!this.isBrowser) return;

        if (this.map) {
            if (this.wheelHandler) {
                this.map.getContainer().removeEventListener('wheel', this.wheelHandler);
            }
            this.map.remove();
        }

        if (this.keydownHandler) {
            window.removeEventListener('keydown', this.keydownHandler);
        }
        if (this.keyupHandler) {
            window.removeEventListener('keyup', this.keyupHandler);
        }
    }
}